import { useEffect, useState } from "react";
import { api, StructureContact } from "../lib/api";
import { ContactDisplay } from "./ContactDisplay";
import { Spinner } from "./Spinner";
import { InlineAlert } from "./InlineAlert";

interface ContactsViewerProps {
  structureId: string;
  contactIds: string[];
  contactRoles?: Record<string, string>;
}

export function ContactsViewer({ structureId, contactIds, contactRoles = {} }: ContactsViewerProps) {
  const [contacts, setContacts] = useState<StructureContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (contactIds.length === 0) {
      setContacts([]);
      setLoading(false);
      return;
    }

    loadContacts();
  }, [structureId, contactIds]);

  const loadContacts = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.listContacts(structureId, false);
      // Filter to only show contacts that are in contactIds
      const filteredContacts = data.contacts.filter(c => contactIds.includes(c.id)).map((c) => {
        const role = contactRoles[c.id];
        return role ? { ...c, contact_type: role } : c;
      });
      // Sort by the order in contactIds
      filteredContacts.sort((a, b) => {
        return contactIds.indexOf(a.id) - contactIds.indexOf(b.id);
      });
      setContacts(filteredContacts);
    } catch (err: any) {
      setError(err.message || "Fehler beim Laden der Kontakte");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner label="Lade Kontakte..." />
      </div>
    );
  }

  if (error) {
    return (
      <InlineAlert tone="error" message={error} dismissible onClose={() => setError(null)} />
    );
  }

  if (contacts.length === 0) {
    return (
      <div className="text-sm text-[var(--color-muted)] text-center py-8">
        Keine Kontakte verknüpft
      </div>
    );
  }

  return (
    <div className="space-y-2 sm:space-y-3">
      {contacts.map(contact => (
        <ContactDisplay key={contact.id} contact={contact} />
      ))}
    </div>
  );
}
